import React, { useEffect, useMemo, useState } from "react";
import {
    Card, CardBody, Col, Row, Badge, Button, Spinner,
    Input, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import SmartPagination from "../../components/Smartpagination";

const api = new APIClient();

/* ── Types ─────────────────────────────────────── */
interface Unit {
    id: number;
    unitNo: string;
    floor: number;
}

interface Payment {
    id: number;
    billId: number;
    amount: number;
    paymentDate: string;
    paymentMode: "CASH" | "UPI" | "ONLINE" | "BANK_TRANSFER";
    transactionId?: string;
    bill: {
        id: number;
        month: number;
        year: number;
        amount: number;
        penalty: number;
        discount: number;
        totalAmount: number;
        unitIds: string;
        unitCount: number;
        units: Unit[];
    };
    receipt?: {
        id: number;
        receiptNo: string;
    };
}

interface Stats {
    totalPaid: number;
    totalPayments: number;
    thisYear: number;
    thisMonth: number;
}

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const PAYMENT_MODE_COLORS: Record<string, string> = {
    CASH: "success",
    UPI: "info",
    ONLINE: "primary",
    BANK_TRANSFER: "warning"
};

const ResidentPayments: React.FC = () => {
    const [payments, setPayments] = useState<Payment[]>([]);
    const [stats, setStats] = useState<Stats>({
        totalPaid: 0,
        totalPayments: 0,
        thisYear: 0,
        thisMonth: 0
    });
    const [loading, setLoading]           = useState(true);
    const [search, setSearch]             = useState("");
    const [monthFilter, setMonthFilter]   = useState<string>("");
    const [yearFilter, setYearFilter]     = useState<string>("");
    const [modeFilter, setModeFilter]     = useState<string>("all");
    const [currentPage, setCurrentPage]   = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewModal, setViewModal]       = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

    useEffect(() => { fetchPayments(); }, []);

    useEffect(() => {
        const timer = setTimeout(() => fetchPayments(), 500);
        return () => clearTimeout(timer);
    }, [monthFilter, yearFilter, modeFilter]);

    /* ── Fetch ── */
    const fetchPayments = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (monthFilter) params.append("month", monthFilter);
            if (yearFilter)  params.append("year", yearFilter);
            if (modeFilter !== "all") params.append("paymentMode", modeFilter);

            const res: any = await api.get(`/api/resident/payments?${params}`);
            setPayments(Array.isArray(res?.data) ? res.data : []);
            setStats(res?.stats || stats);
        } catch {
            toast.error("Failed to load payment history");
        } finally {
            setLoading(false);
        }
    };

    /* ── Local Search Filter ── */
    const filteredPayments = useMemo(() => {
        if (!search.trim()) return payments;
        const term = search.toLowerCase();
        return payments.filter(p => {
            const text = [
                p.receipt?.receiptNo || "",
                p.transactionId || "",
                MONTH_NAMES[p.bill.month - 1],
                String(p.bill.year),
                String(p.amount),
                p.paymentMode,
                ...(p.bill.units?.map(u => u.unitNo) || [])
            ].join(" ").toLowerCase();
            return text.includes(term);
        });
    }, [payments, search]);

    /* ── Smart Pagination ── */
    const totalPages   = Math.max(1, Math.ceil(filteredPayments.length / itemsPerPage));
    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredPayments.slice(indexOfFirst, indexOfFirst + itemsPerPage);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    /* ── Years for filter ── */
    const years = useMemo(() => {
        const set = new Set(payments.map(p => p.bill.year));
        return Array.from(set).sort((a, b) => b - a);
    }, [payments]);

    /* ── Reset Filters ── */
    const resetFilters = () => {
        setMonthFilter("");
        setYearFilter("");
        setModeFilter("all");
        setSearch("");
        setCurrentPage(1);
    };

    /* ── Download Receipt ── */
    const downloadReceipt = async (payment: Payment) => {
        if (!payment.receipt) { toast.error("Receipt not available"); return; }
        try {
            toast.info("Generating receipt...");
            const token = localStorage.getItem("residentToken");
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/receipts/download/${payment.billId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Failed");
            const blob = await response.blob();
            const url  = window.URL.createObjectURL(blob);
            const a    = document.createElement("a");
            a.href     = url;
            a.download = `Receipt_${payment.receipt.receiptNo}.pdf`;
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Downloaded! 📄");
        } catch {
            toast.error("Download failed");
        }
    };

    /* ── Export Excel ── */
    const exportExcel = () => {
        const data = filteredPayments.map((p, i) => ({
            "Sr":             i + 1,
            "Receipt No":     p.receipt?.receiptNo || "—",
            "Date":           new Date(p.paymentDate).toLocaleDateString("en-IN"),
            "Period":         `${MONTH_NAMES[p.bill.month - 1]} ${p.bill.year}`,
            "Units":          p.bill.units?.map(u => u.unitNo).join(", ") || "—",
            "Unit Count":     p.bill.unitCount,
            "Base Amount":    p.bill.amount,
            "Penalty":        p.bill.penalty,
            "Discount":       p.bill.discount,
            "Total Paid":     p.amount,
            "Mode":           p.paymentMode,
            "Transaction ID": p.transactionId || "—",
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "My Payments");
        saveAs(
            new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })],
                { type: "application/octet-stream" }),
            "My_Payment_History.xlsx"
        );
        toast.success("Excel exported");
    };

    /* ── Loading ── */
    if (loading) return (
        <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Loading payment history...</p>
        </div>
    );

    /* ── RENDER ── */
    return (
        <div style={{ maxWidth: "100%", overflowX: "hidden" }}>

            {/* ── Page Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ fontSize: "clamp(16px, 4vw, 22px)" }}>
                        <i className="ri-wallet-3-line me-2 text-primary" />
                        Payment History
                    </h4>
                    <small className="text-muted">
                        View all your past payments and download receipts
                    </small>
                </div>
                <Button color="success" outline size="sm" onClick={exportExcel}
                    style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                    <i className="ri-file-excel-2-line me-1" />Export Excel
                </Button>
            </div>

            {/* ── Stats Cards ── */}
            <Row className="g-2 g-md-3 mb-4">
                {[
                    {
                        label: "Total Paid",
                        value: `₹${stats.totalPaid.toLocaleString("en-IN")}`,
                        icon: "ri-money-rupee-circle-line",
                        color: "#16a34a",
                        bg: "linear-gradient(135deg,#dcfce7,#bbf7d0)"
                    },
                    {
                        label: "Total Payments",
                        value: stats.totalPayments,
                        icon: "ri-bill-line",
                        color: "#185FA5",
                        bg: "linear-gradient(135deg,#dbeafe,#bfdbfe)"
                    },
                    {
                        label: "This Year",
                        value: `₹${stats.thisYear.toLocaleString("en-IN")}`,
                        icon: "ri-calendar-2-line",
                        color: "#7c3aed",
                        bg: "linear-gradient(135deg,#ede9fe,#ddd6fe)"
                    },
                    {
                        label: "This Month",
                        value: `₹${stats.thisMonth.toLocaleString("en-IN")}`,
                        icon: "ri-calendar-line",
                        color: "#d97706",
                        bg: "linear-gradient(135deg,#fef3c7,#fde68a)"
                    },
                ].map((s, i) => (
                    <Col key={i} xs={6} md={3}>
                        <Card className="stat-card border-0 h-100"
                            style={{ borderRadius: 12, "--stat-color": s.color } as React.CSSProperties}>
                            <CardBody style={{ background: s.bg, borderRadius: 12, padding: "12px 14px" }}>
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10,
                                        background: "#fff", flexShrink: 0,
                                        display: "flex", alignItems: "center",
                                        justifyContent: "center",
                                        color: s.color, fontSize: 20,
                                    }}>
                                        <i className={s.icon} />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{
                                            fontSize: "clamp(13px, 3vw, 18px)",
                                            fontWeight: 700, color: s.color,
                                            lineHeight: 1.2, whiteSpace: "nowrap",
                                            overflow: "hidden", textOverflow: "ellipsis",
                                        }}>
                                            {s.value}
                                        </div>
                                        <div style={{
                                            fontSize: "clamp(9px, 2vw, 11px)",
                                            color: "#475569",
                                        }}>
                                            {s.label}
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* ── Filters Card ── */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12, background: "var(--bg-card)" }}>
                <CardBody style={{ padding: "14px 16px" }}>
                    <Row className="g-2">
                        <Col xs={6} md={3}>
                            <label className="text-muted small mb-1">Month</label>
                            <Input type="select" value={monthFilter}
                                onChange={e => { setMonthFilter(e.target.value); setCurrentPage(1); }}
                                style={{ fontSize: 13, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}>
                                <option value="">All Months</option>
                                {MONTH_NAMES.map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs={6} md={2}>
                            <label className="text-muted small mb-1">Year</label>
                            <Input type="select" value={yearFilter}
                                onChange={e => { setYearFilter(e.target.value); setCurrentPage(1); }}
                                style={{ fontSize: 13, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}>
                                <option value="">All Years</option>
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs={6} md={3}>
                            <label className="text-muted small mb-1">Search</label>
                            <Input type="text" placeholder="Receipt, amount, unit..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                style={{ fontSize: 13, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }} />
                        </Col>
                        <Col xs={12} md={1} className="d-flex align-items-end">
                            <Button color="light" size="sm" block onClick={resetFilters}
                                style={{ fontSize: 13 }}>
                                <i className="ri-refresh-line me-1" />
                                <span className="d-none d-md-inline">Reset</span>
                                <span className="d-inline d-md-none">Reset Filters</span>
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* ── Payment List ── */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: 12, background: "var(--bg-card)" }}>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h6 className="fw-bold mb-0">
                            All Payments
                            <Badge color="info" pill className="ms-2" style={{ fontSize: 11 }}>
                                {filteredPayments.length}
                            </Badge>
                        </h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Showing {filteredPayments.length === 0 ? 0 : indexOfFirst + 1}–{Math.min(indexOfFirst + itemsPerPage, filteredPayments.length)} of {filteredPayments.length}
                        </span>
                    </div>

                    {currentItems.length > 0 ? (
                        currentItems.map(payment => (
                            <div
                                key={payment.id}
                                onClick={() => { setSelectedPayment(payment); setViewModal(true); }}
                                style={{
                                    padding: "12px 14px",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: 12,
                                    background: "var(--bg-item)",
                                    marginBottom: 10,
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.borderColor = "#764ba2";
                                    e.currentTarget.style.boxShadow = "var(--card-shadow)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.borderColor = "var(--border-color)";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                            >
                                {/* ── Mobile Layout ── */}
                                <div className="d-flex d-md-none align-items-start gap-3">
                                    {/* Icon */}
                                    <div style={{
                                        width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                                        background: "linear-gradient(135deg,#16a34a,#15803d)",
                                        display: "flex", alignItems: "center",
                                        justifyContent: "center", color: "#fff", fontSize: 16,
                                    }}>
                                        <i className="ri-check-line" />
                                    </div>

                                    {/* Content */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <div>
                                                <span className="fw-bold" style={{ fontSize: 14 }}>
                                                    {MONTH_NAMES[payment.bill.month - 1]} {payment.bill.year}
                                                </span>
                                                <Badge color={PAYMENT_MODE_COLORS[payment.paymentMode]}
                                                    pill className="ms-1" style={{ fontSize: 9 }}>
                                                    {payment.paymentMode}
                                                </Badge>
                                            </div>
                                            <span className="fw-bold" style={{ fontSize: 16, color: "#16a34a" }}>
                                                ₹{payment.amount.toLocaleString("en-IN")}
                                            </span>
                                        </div>

                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                            <i className="ri-calendar-line me-1" />
                                            {new Date(payment.paymentDate).toLocaleDateString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric"
                                            })}
                                        </div>

                                        {payment.bill.units?.length > 0 && (
                                            <div className="text-primary mt-1" style={{ fontSize: 11 }}>
                                                <i className="ri-home-4-line me-1" />
                                                {payment.bill.unitCount} unit{payment.bill.unitCount > 1 ? "s" : ""}:
                                            </div>
                                        )}

                                        {payment.receipt && (
                                            <div className="text-success mt-1" style={{ fontSize: 11 }}>
                                                <i className="ri-file-list-line me-1" />
                                                {payment.receipt.receiptNo}
                                            </div>
                                        )}

                                        {/* Mobile Action */}
                                        {payment.receipt && (
                                            <Button color="success" size="sm"
                                                className="mt-2"
                                                onClick={e => { e.stopPropagation(); downloadReceipt(payment); }}
                                                style={{ fontSize: 11, padding: "3px 10px" }}>
                                                <i className="ri-download-line me-1" />Receipt
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* ── Desktop Layout ── */}
                                <Row className="align-items-center d-none d-md-flex">
                                    {/* Icon */}
                                    <Col md={1}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: "50%",
                                            background: "linear-gradient(135deg,#16a34a,#15803d)",
                                            display: "flex", alignItems: "center",
                                            justifyContent: "center", color: "#fff", fontSize: 18,
                                        }}>
                                            <i className="ri-check-line" />
                                        </div>
                                    </Col>

                                    {/* Info */}
                                    <Col md={4}>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-bold" style={{ fontSize: 15 }}>
                                                {MONTH_NAMES[payment.bill.month - 1]} {payment.bill.year}
                                            </span>
                                            <Badge color={PAYMENT_MODE_COLORS[payment.paymentMode]}
                                                pill style={{ fontSize: 10 }}>
                                                {payment.paymentMode}
                                            </Badge>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            <i className="ri-calendar-line me-1" />
                                            Paid on: {new Date(payment.paymentDate).toLocaleDateString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric"
                                            })}
                                        </div>
                                        {payment.bill.units?.length > 0 && (
                                            <div className="text-primary mt-1" style={{ fontSize: 11 }}>
                                                <i className="ri-home-4-line me-1" />
                                                {payment.bill.unitCount} units
                                            </div>
                                        )}
                                        {payment.receipt && (
                                            <div className="text-success mt-1" style={{ fontSize: 11 }}>
                                                <i className="ri-file-list-line me-1" />
                                                {payment.receipt.receiptNo}
                                            </div>
                                        )}
                                    </Col>

                                    {/* Amount Breakdown */}
                                    <Col md={3} className="text-center">
                                        {payment.bill.penalty > 0 ? (
                                            <div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>Base: ₹{payment.bill.amount}</div>
                                                <div className="text-danger" style={{ fontSize: 11 }}>+Penalty: ₹{payment.bill.penalty}</div>
                                            </div>
                                        ) : payment.bill.discount > 0 ? (
                                            <div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>Base: ₹{payment.bill.amount}</div>
                                                <div className="text-success" style={{ fontSize: 11 }}>-Discount: ₹{payment.bill.discount}</div>
                                            </div>
                                        ) : (
                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                Base: ₹{payment.bill.amount}
                                            </div>
                                        )}
                                    </Col>

                                    {/* Total */}
                                    <Col md={2} className="text-end">
                                        <div className="fw-bold" style={{ fontSize: 22, color: "#16a34a" }}>
                                            ₹{payment.amount.toLocaleString("en-IN")}
                                        </div>
                                    </Col>

                                    {/* Action */}
                                    <Col md={2} className="text-end">
                                        {payment.receipt && (
                                            <Button color="success" size="sm"
                                                onClick={e => { e.stopPropagation(); downloadReceipt(payment); }}
                                                style={{ fontWeight: 600, fontSize: 12 }}>
                                                <i className="ri-download-line me-1" />Receipt
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-5">
                            <div style={{ fontSize: 60, opacity: 0.3 }}>💳</div>
                            <h6 className="text-muted mt-2">No payments found</h6>
                            <p className="text-muted" style={{ fontSize: 13 }}>
                                {search || monthFilter || yearFilter
                                    ? "Try changing your filters"
                                    : "Your payment history will appear here"}
                            </p>
                            {(search || monthFilter || yearFilter || modeFilter !== "all") && (
                                <Button color="primary" size="sm" outline onClick={resetFilters}>
                                    <i className="ri-refresh-line me-1" />Reset Filters
                                </Button>
                            )}
                        </div>
                    )}

                    {/* ── Smart Pagination ── */}
                    {!loading && filteredPayments.length > 0 && (
                        <SmartPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={filteredPayments.length}
                            itemsPerPage={itemsPerPage}
                            indexOfFirst={indexOfFirst}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={size => {
                                setItemsPerPage(size);
                                setCurrentPage(1);
                            }}
                            itemLabel="payments"
                            pageSizeOptions={[5, 10, 20, 50]}
                        />
                    )}
                </CardBody>
            </Card>

            {/* ── View Modal ── */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)} centered size="md" contentClassName="bg-card">
                <ModalHeader toggle={() => setViewModal(false)} className="border-0">
                    <span className="fw-bold" style={{ color: "var(--text-primary)" }}>
                        <i className="ri-wallet-3-line me-2 text-primary" />Payment Details
                    </span>
                </ModalHeader>
                <ModalBody style={{ padding: "16px 20px" }}>
                    {selectedPayment && (
                        <div>
                            {/* Hero */}
                            <div style={{
                                background: "rgba(22, 163, 74, 0.1)",
                                padding: "20px 16px", borderRadius: 12,
                                textAlign: "center", marginBottom: 16,
                            }}>
                                <Badge color="success" pill className="mb-2" style={{ fontSize: 12 }}>
                                    ✓ PAID
                                </Badge>
                                <div className="fw-bold" style={{
                                    fontSize: "clamp(28px, 8vw, 40px)",
                                    color: "#16a34a"
                                }}>
                                    ₹{selectedPayment.amount.toLocaleString("en-IN")}
                                </div>
                                {selectedPayment.receipt && (
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        Receipt: <strong>{selectedPayment.receipt.receiptNo}</strong>
                                    </div>
                                )}
                            </div>

                            <table className="table table-borderless" style={{ fontSize: 13, color: "var(--text-primary)" }}>
                                <tbody>
                                    <tr>
                                        <td className="text-muted" style={{ width: "45%" }}>Period:</td>
                                        <td className="text-end fw-bold">
                                            {MONTH_NAMES[selectedPayment.bill.month - 1]} {selectedPayment.bill.year}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">
                                            Units :
                                        </td>
                                        <td className="text-end">
                                            <div className="d-flex flex-wrap gap-1 justify-content-end">
                                                {selectedPayment.bill.unitCount}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: "1px solid var(--border-color)" }}>
                                        <td className="text-muted">Base Amount:</td>
                                        <td className="text-end">₹{selectedPayment.bill.amount}</td>
                                    </tr>
                                    {selectedPayment.bill.penalty > 0 && (
                                        <tr>
                                            <td className="text-muted">Penalty:</td>
                                            <td className="text-end text-danger">
                                                +₹{selectedPayment.bill.penalty}
                                            </td>
                                        </tr>
                                    )}
                                    {selectedPayment.bill.discount > 0 && (
                                        <tr>
                                            <td className="text-muted">Discount:</td>
                                            <td className="text-end text-success">
                                                -₹{selectedPayment.bill.discount}
                                            </td>
                                        </tr>
                                    )}
                                    <tr style={{ borderTop: "2px solid #16a34a" }}>
                                        <td className="fw-bold">Total Paid:</td>
                                        <td className="text-end fw-bold"
                                            style={{ color: "#16a34a", fontSize: 16 }}>
                                            ₹{selectedPayment.amount}
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: "1px solid var(--border-color)" }}>
                                        <td className="text-muted">Payment Mode:</td>
                                        <td className="text-end">
                                            <Badge color={PAYMENT_MODE_COLORS[selectedPayment.paymentMode]} pill>
                                                {selectedPayment.paymentMode}
                                            </Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Payment Date:</td>
                                        <td className="text-end" style={{ fontSize: 12 }}>
                                            {new Date(selectedPayment.paymentDate).toLocaleString("en-IN", {
                                                day: "2-digit", month: "long", year: "numeric",
                                                hour: "2-digit", minute: "2-digit",
                                            })}
                                        </td>
                                    </tr>
                                    {selectedPayment.transactionId && (
                                        <tr>
                                            <td className="text-muted">Transaction ID:</td>
                                            <td className="text-end">
                                                <code style={{ fontSize: 11 }}>
                                                    {selectedPayment.transactionId}
                                                </code>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-0 flex-wrap gap-2">
                    {selectedPayment?.receipt && (
                        <Button color="success" onClick={() => {
                            downloadReceipt(selectedPayment);
                            setViewModal(false);
                        }}>
                            <i className="ri-download-line me-1" />Download Receipt
                        </Button>
                    )}
                    <Button color="primary" onClick={() => setViewModal(false)}>Close</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ResidentPayments;